import { FREESTAR_STICKY_FOOTER_PLACEMENT } from './placements';

// https://freestarhelp.zendesk.com/hc/en-us/articles/34516941197460-Disabling-Products

export type FreestarDisabledProducts = {
  googleInterstitial?: boolean;
  stickyFooter?: boolean;
  stickyRail?: boolean;
};

type FreestarWindowConfig = {
  channel?: string;
  disabledProducts?: FreestarDisabledProducts;
  enabled_slots?: unknown[];
};

type FreestarIdentityPayload = { email: string } | { hashes: { md5?: string; sha1?: string; sha256?: string } };

type FreestarPlacementConfig = {
  name?: string;
  stickyRailOptions?: {
    active?: boolean;
    isOnCall?: boolean;
  };
};

type FreestarWindow = {
  queue?: Array<() => void>;
  config?: FreestarWindowConfig;
  deleteStickyFooter?: (placementName: string) => void;
  newStickyFooter?: (placementName: string) => void;
  fsdata?: {
    placements?: FreestarPlacementConfig[];
  };
  identity?: {
    setIdentity?: (payload: FreestarIdentityPayload) => void;
  };
};

declare global {
  interface Window {
    freestar?: FreestarWindow;
  }
}

let stickyFooterDisabledForPath: string | null = null;
let stickyFooterDeletedByPaywall = false;
let lastStickyFooterDisabled: boolean | null = null;

const isStickyFooterLockedForCurrentPath = (): boolean =>
  typeof window !== 'undefined' && stickyFooterDisabledForPath === window.location.pathname;

const queueDeleteStickyFooter = (freestar: FreestarWindow): void => {
  freestar.queue = freestar.queue || [];
  freestar.queue.push(() => {
    freestar.deleteStickyFooter?.(FREESTAR_STICKY_FOOTER_PLACEMENT);
  });
};

const queueNewStickyFooter = (freestar: FreestarWindow): void => {
  freestar.queue = freestar.queue || [];
  freestar.queue.push(() => {
    freestar.newStickyFooter?.(FREESTAR_STICKY_FOOTER_PLACEMENT);
  });
};

const syncStickyFooterProduct = (freestar: FreestarWindow, shouldDisable: boolean): void => {
  if (shouldDisable) {
    if (lastStickyFooterDisabled !== true) {
      queueDeleteStickyFooter(freestar);
    }
    lastStickyFooterDisabled = true;
    return;
  }

  const shouldReinject = lastStickyFooterDisabled === true || stickyFooterDeletedByPaywall;
  stickyFooterDeletedByPaywall = false;
  stickyFooterDisabledForPath = null;
  lastStickyFooterDisabled = false;

  if (shouldReinject) {
    queueNewStickyFooter(freestar);
  }
};

export const ensureFreestarConfig = ({
  channel,
  disabledProducts,
}: {
  channel: string;
  disabledProducts?: FreestarDisabledProducts;
}): void => {
  if (typeof window === 'undefined') return;

  const freestar = (window.freestar = window.freestar || {});
  freestar.queue = freestar.queue || [];
  freestar.config = freestar.config || {};
  freestar.config.channel = channel;

  const shouldDisableStickyFooter = !!disabledProducts?.stickyFooter || isStickyFooterLockedForCurrentPath();

  // https://freestarhelp.zendesk.com/hc/en-us/articles/34516941197460-Disabling-Products
  freestar.config.disabledProducts = {
    ...(freestar.config.disabledProducts || {}),
    googleInterstitial: !!disabledProducts?.googleInterstitial,
    stickyFooter: shouldDisableStickyFooter,
    stickyRail: !!disabledProducts?.stickyRail,
  };

  syncStickyFooterProduct(freestar, shouldDisableStickyFooter);
};

const STATIC_SIDEBAR_PLACEMENT = 'benzinga-com_sidebar_1';

const clearStaticSidebarStickyRail = (freestar: FreestarWindow): void => {
  const placement = freestar.fsdata?.placements?.find(entry => entry.name === STATIC_SIDEBAR_PLACEMENT);
  if (placement?.stickyRailOptions?.active) {
    placement.stickyRailOptions.active = false;
  }
};

// PubOS marks benzinga-com_sidebar_1 as Sticky Rail. While that flag is active,
// freestar.newAdSlots silently drops the placement — so in-layout React slots
// never fill. Clear it once pubfig has loaded so our ArticleSidebar mounts win.
export const neutralizeFreestarStickyRailForReactSlots = (): void => {
  if (typeof window === 'undefined') return;

  const freestar = (window.freestar = window.freestar || {});
  freestar.queue = freestar.queue || [];

  if (freestar.fsdata?.placements?.length) {
    clearStaticSidebarStickyRail(freestar);
    return;
  }

  freestar.queue.push(() => {
    clearStaticSidebarStickyRail(freestar);
  });
};

// https://freestarhelp.zendesk.com/hc/en-us/articles/34516941197460-Disabling-Products
export const disableFreestarStickyFooter = (): void => {
  if (typeof window === 'undefined') return;

  stickyFooterDisabledForPath = window.location.pathname;
  stickyFooterDeletedByPaywall = true;
  lastStickyFooterDisabled = true;

  const freestar = (window.freestar = window.freestar || {});
  freestar.queue = freestar.queue || [];
  freestar.config = freestar.config || {};
  freestar.config.disabledProducts = {
    ...(freestar.config.disabledProducts || {}),
    stickyFooter: true,
  };

  queueDeleteStickyFooter(freestar);
};

export const resetFreestarStickyFooterPaywallLockForTests = (): void => {
  stickyFooterDisabledForPath = null;
  stickyFooterDeletedByPaywall = false;
  lastStickyFooterDisabled = null;
};
